import { AudioEngineError } from './errors';
import type {
  AudioEngineOptions,
  AudioEngineSnapshot,
  AudioOutputMode,
  DecodedTrack,
  AudioTrackState,
  TrackInput,
} from '../types/audio';

interface TrackRuntime extends DecodedTrack {
  gainNode: GainNode;
  panNode: StereoPannerNode;
  monoNode: GainNode;
  analyserNode: AnalyserNode;
  source: AudioBufferSourceNode | null;
}

type SnapshotListener = (snapshot: AudioEngineSnapshot) => void;

const DEFAULT_SCHEDULE_AHEAD = 0.03;
const DEFAULT_MEMORY_WARNING = 512 * 1024 * 1024;

/**
 * Motor de reproducción multitrack independiente de React.
 * Cada inicio, reanudación o búsqueda crea nuevos AudioBufferSourceNode.
 */
export class AudioEngine {
  private readonly context: AudioContext;
  private readonly scheduleAheadSeconds: number;
  private readonly memoryWarningBytes: number;
  private readonly masterGain: GainNode;
  private readonly outputMerger: ChannelMergerNode;
  private tracks = new Map<string, TrackRuntime>();
  private listeners = new Set<SnapshotListener>();
  private outputMode: AudioOutputMode = 'stereo';
  private status: AudioEngineSnapshot['status'] = 'idle';
  private duration = 0;
  private pausedAt = 0;
  private startedAt = 0;
  private masterVolume = 1;
  private memoryBytes = 0;
  private warning: string | null = null;
  private error: string | null = null;
  private playbackGeneration = 0;

  constructor(options: AudioEngineOptions = {}) {
    const AudioContextConstructor = globalThis.AudioContext;
    if (!options.context && !AudioContextConstructor) {
      throw new AudioEngineError('Este navegador no ofrece Web Audio API.', 'CONTEXT_FAILED');
    }

    this.context = options.context ?? new AudioContextConstructor();
    this.scheduleAheadSeconds = options.scheduleAheadSeconds ?? DEFAULT_SCHEDULE_AHEAD;
    this.memoryWarningBytes = options.memoryWarningBytes ?? DEFAULT_MEMORY_WARNING;
    this.masterGain = this.context.createGain();
    this.outputMerger = this.context.createChannelMerger(2);
    this.masterGain.gain.value = this.masterVolume;
    this.masterGain.connect(this.context.destination);
  }

  async decodeTracks(inputs: TrackInput[]): Promise<DecodedTrack[]> {
    if (inputs.length === 0) {
      throw new AudioEngineError('La canción no contiene pistas.', 'NO_TRACKS');
    }

    this.setStatus('loading');
    try {
      const tracks = await Promise.all(
        inputs.map(async ({ file, ...metadata }) => {
          try {
            const bytes = await file.arrayBuffer();
            const buffer = await this.context.decodeAudioData(bytes.slice(0));
            if (!Number.isFinite(buffer.duration) || buffer.duration <= 0) {
              throw new AudioEngineError(
                `La pista “${metadata.name}” no tiene una duración válida.`,
                'INVALID_DURATION',
              );
            }
            return { ...metadata, buffer };
          } catch (cause) {
            if (cause instanceof AudioEngineError) throw cause;
            throw new AudioEngineError(
              `No se pudo decodificar “${metadata.name}”. Comprueba que sea un WAV compatible.`,
              'DECODE_FAILED',
              { cause },
            );
          }
        }),
      );
      return tracks;
    } catch (cause) {
      this.fail(cause);
      throw cause;
    }
  }

  loadTracks(decodedTracks: DecodedTrack[]): void {
    if (decodedTracks.length === 0) {
      throw new AudioEngineError('La canción no contiene pistas.', 'NO_TRACKS');
    }
    if (decodedTracks.some((track) => !track.buffer)) {
      throw new AudioEngineError('Falta al menos un archivo de audio.', 'MISSING_TRACK');
    }

    this.releaseTracks();
    for (const track of decodedTracks) {
      const gainNode = this.context.createGain();
      const panNode = this.context.createStereoPanner();
      const monoNode = this.context.createGain();
      const analyserNode = this.context.createAnalyser();
      monoNode.channelCount = 1;
      monoNode.channelCountMode = 'explicit';
      monoNode.channelInterpretation = 'speakers';
      analyserNode.fftSize = 256;
      analyserNode.smoothingTimeConstant = 0.8;
      this.tracks.set(track.id, {
        ...track,
        volume: clamp(track.volume, 0, 1),
        pan: clamp(track.pan, -1, 1),
        gainNode,
        panNode,
        monoNode,
        analyserNode,
        source: null,
      });
    }

    this.duration = Math.max(...decodedTracks.map((track) => track.buffer.duration));
    this.memoryBytes = decodedTracks.reduce(
      (total, track) => total + track.buffer.length * track.buffer.numberOfChannels * 4,
      0,
    );
    this.warning =
      this.memoryBytes >= this.memoryWarningBytes
        ? `La canción requiere aproximadamente ${formatMegabytes(this.memoryBytes)} MB de memoria decodificada.`
        : null;
    this.pausedAt = 0;
    this.error = null;
    this.connectRouting();
    this.applyAllTrackLevels();
    this.setStatus('ready');
  }

  async play(): Promise<void> {
    if (this.tracks.size === 0) {
      throw new AudioEngineError('Carga una canción antes de reproducir.', 'NO_TRACKS');
    }
    if (this.status === 'playing') return;
    if (this.context.state === 'suspended') await this.context.resume();
    if (this.pausedAt >= this.duration) this.pausedAt = 0;

    const generation = ++this.playbackGeneration;
    const startAt = this.context.currentTime + this.scheduleAheadSeconds;
    this.startedAt = startAt - this.pausedAt;

    for (const track of this.tracks.values()) {
      const source = this.context.createBufferSource();
      source.buffer = track.buffer;
      source.connect(track.gainNode);
      track.source = source;
      if (this.pausedAt < track.buffer.duration) {
        source.start(startAt, this.pausedAt);
      }
    }

    const longest = [...this.tracks.values()].reduce((current, track) =>
      track.buffer.duration > current.buffer.duration ? track : current,
    );
    if (longest.source) {
      longest.source.onended = () => {
        if (generation !== this.playbackGeneration || this.status !== 'playing') return;
        this.stopSources();
        this.pausedAt = 0;
        this.setStatus('ready');
      };
    }
    this.setStatus('playing');
  }

  pause(): void {
    if (this.status !== 'playing') return;
    this.pausedAt = this.currentPosition();
    this.stopSources();
    this.setStatus('paused');
  }

  stop(): void {
    this.stopSources();
    this.pausedAt = 0;
    if (this.tracks.size > 0) this.setStatus('ready');
  }

  async seek(seconds: number): Promise<void> {
    const nextPosition = clamp(seconds, 0, this.duration);
    const wasPlaying = this.status === 'playing';
    this.stopSources();
    this.pausedAt = nextPosition;
    if (wasPlaying) this.status = 'paused';
    this.emit();
    if (wasPlaying) await this.play();
  }

  setTrackVolume(trackId: string, volume: number): void {
    const track = this.requireTrack(trackId);
    track.volume = clamp(volume, 0, 1);
    this.applyAllTrackLevels();
  }

  setTrackMuted(trackId: string, muted: boolean): void {
    this.requireTrack(trackId).muted = muted;
    this.applyAllTrackLevels();
  }

  setTrackSolo(trackId: string, solo: boolean): void {
    this.requireTrack(trackId).solo = solo;
    this.applyAllTrackLevels();
  }

  setTrackPan(trackId: string, pan: number): void {
    const track = this.requireTrack(trackId);
    track.pan = clamp(pan, -1, 1);
    track.panNode.pan.value = track.pan;
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = clamp(volume, 0, 1);
    this.masterGain.gain.value = this.masterVolume;
    this.emit();
  }

  async setOutputMode(mode: AudioOutputMode): Promise<void> {
    if (mode === this.outputMode) return;
    const wasPlaying = this.status === 'playing';
    if (wasPlaying) {
      this.pausedAt = this.currentPosition();
      this.stopSources();
      this.status = 'paused';
    }
    this.outputMode = mode;
    this.connectRouting();
    this.emit();
    if (wasPlaying) await this.play();
  }

  getSnapshot(): AudioEngineSnapshot {
    return {
      status: this.status,
      position: this.currentPosition(),
      duration: this.duration,
      outputMode: this.outputMode,
      masterVolume: this.masterVolume,
      memoryBytes: this.memoryBytes,
      warning: this.warning,
      error: this.error,
    };
  }

  getTracks(): AudioTrackState[] {
    return [...this.tracks.values()].map(
      ({ id, name, role, volume, muted, solo, pan, buffer }) => ({
        id,
        name,
        role,
        volume,
        muted,
        solo,
        pan,
        duration: buffer.duration,
      }),
    );
  }
  getTrackLevel(trackId: string): number {
    const track = this.tracks.get(trackId);
    if (!track) return 0;
    const samples = new Float32Array(track.analyserNode.fftSize);
    track.analyserNode.getFloatTimeDomainData(samples);
    let peak = 0;
    for (const sample of samples) peak = Math.max(peak, Math.abs(sample));
    return Math.min(1, peak);
  }
  subscribe(listener: SnapshotListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  async dispose(): Promise<void> {
    this.releaseTracks();
    this.masterGain.disconnect();
    this.outputMerger.disconnect();
    this.listeners.clear();
    if (this.context.state !== 'closed') await this.context.close();
  }

  private currentPosition(): number {
    if (this.status !== 'playing') return this.pausedAt;
    return clamp(this.context.currentTime - this.startedAt, 0, this.duration);
  }

  private connectRouting(): void {
    this.outputMerger.disconnect();
    for (const track of this.tracks.values()) {
      track.gainNode.disconnect();
      track.panNode.disconnect();
      track.monoNode.disconnect();
      track.analyserNode.disconnect();
      track.panNode.pan.value = track.pan;
      if (this.outputMode === 'stereo') {
        track.gainNode.connect(track.analyserNode);
        track.analyserNode.connect(track.panNode);
        track.panNode.connect(this.masterGain);
      } else {
        track.gainNode.connect(track.analyserNode);
        track.analyserNode.connect(track.monoNode);
        const channel = track.role === 'music' ? 0 : 1;
        track.monoNode.connect(this.outputMerger, 0, channel);
      }
    }
    if (this.outputMode === 'music-click') this.outputMerger.connect(this.masterGain);
  }

  private applyAllTrackLevels(): void {
    const hasSolo = [...this.tracks.values()].some((track) => track.solo);
    for (const track of this.tracks.values()) {
      const audible = !track.muted && (!hasSolo || track.solo);
      track.gainNode.gain.value = audible ? track.volume : 0;
    }
    this.emit();
  }

  private requireTrack(trackId: string): TrackRuntime {
    const track = this.tracks.get(trackId);
    if (!track) throw new Error(`No existe la pista ${trackId}.`);
    return track;
  }

  private stopSources(): void {
    this.playbackGeneration += 1;
    for (const track of this.tracks.values()) {
      if (!track.source) continue;
      track.source.onended = null;
      try {
        track.source.stop();
      } catch {
        // Un nodo que aún no empezó o ya finalizó puede rechazar stop().
      }
      track.source.disconnect();
      track.source = null;
    }
  }

  private releaseTracks(): void {
    this.stopSources();
    for (const track of this.tracks.values()) {
      track.gainNode.disconnect();
      track.panNode.disconnect();
      track.monoNode.disconnect();
      track.analyserNode.disconnect();
    }
    this.tracks.clear();
    this.duration = 0;
    this.memoryBytes = 0;
    this.warning = null;
    this.pausedAt = 0;
    this.status = 'idle';
  }

  private setStatus(status: AudioEngineSnapshot['status']): void {
    this.status = status;
    this.emit();
  }

  private fail(cause: unknown): void {
    this.error = cause instanceof Error ? cause.message : 'Ocurrió un error de audio.';
    this.setStatus('error');
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function formatMegabytes(bytes: number): string {
  return (bytes / 1024 / 1024).toFixed(0);
}
